import React, { useMemo, useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { Pencil, RefreshCw } from "lucide-react";
import { useDispatch } from "react-redux";
import BorderedSelect, { BorderedSelectOption } from "src/components/form-new/bordered-select";
import BorderedTextarea from "src/components/form-new/bordered-textarea";
import { Button } from "src/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "src/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  useGetSKUsQuery,
  usePatchSKUMutation,
  useSyncSKUsMutation,
} from "src/redux/billing/billing.endpoints";
import type { SKU, SKUPatchPayload } from "src/redux/billing/billing.types";
import { showNotification } from "src/redux/notification/notification.slice";

const BILL_TYPE_SELECT_OPTIONS: BorderedSelectOption[] = [
  { value: "bill_in_full", label: "Bill in full" },
  { value: "pass_through", label: "Pass through" },
  { value: "do_not_bill", label: "Do not bill" },
];

const STATUS_SELECT_OPTIONS: BorderedSelectOption[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

type EditDraft = {
  status: string;
  keywords: string;
  bill_type: string;
  qty_is_one: boolean;
};

function skuToDraft(row: SKU): EditDraft {
  return {
    status: row.status ?? "",
    keywords: row.keywords ?? "",
    bill_type: row.bill_type ?? "",
    qty_is_one: row.qty_is_one,
  };
}

function draftToPayload(draft: EditDraft): SKUPatchPayload {
  return {
    status: draft.status.trim() || null,
    keywords: draft.keywords.trim() || null,
    bill_type: draft.bill_type.trim() || null,
    qty_is_one: draft.qty_is_one,
  };
}

/** Active first, then other non-archived statuses, archived last. */
function statusSortRank(status: string | null): number {
  const s = (status ?? "").toLowerCase();
  if (s === "archived") return 2;
  if (s === "active") return 0;
  return 1;
}

const ProductsTab: React.FC = () => {
  const dispatch = useDispatch();
  const { data: skus = [], isLoading, isError, refetch } = useGetSKUsQuery({ includeArchived: true });
  const [patchSKU, { isLoading: isSaving }] = usePatchSKUMutation();
  const [syncSKUs, { isLoading: isSyncing }] = useSyncSKUsMutation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<SKU | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);

  const sortedSkus = useMemo(() => {
    return [...skus].sort((a, b) => {
      const ra = statusSortRank(a.status);
      const rb = statusSortRank(b.status);
      if (ra !== rb) return ra - rb;
      if (ra === 1) {
        const sa = (a.status ?? "").toLowerCase();
        const sb = (b.status ?? "").toLowerCase();
        const cmp = sa.localeCompare(sb);
        if (cmp !== 0) return cmp;
      }
      const an = (a.name ?? a.sku ?? "").toLowerCase();
      const bn = (b.name ?? b.sku ?? "").toLowerCase();
      return an.localeCompare(bn);
    });
  }, [skus]);

  const billTypeOptions = useMemo(() => {
    const opts = [...BILL_TYPE_SELECT_OPTIONS];
    const raw = draft?.bill_type?.trim() ?? "";
    if (raw && !opts.some((o) => o.value === raw)) {
      opts.push({ value: raw, label: raw });
    }
    return opts;
  }, [draft?.bill_type]);

  const statusOptions = useMemo(() => {
    const opts = [...STATUS_SELECT_OPTIONS];
    const raw = draft?.status?.trim() ?? "";
    if (raw && !opts.some((o) => o.value === raw)) {
      opts.push({ value: raw, label: raw });
    }
    return opts;
  }, [draft?.status]);

  useEffect(() => {
    if (editing && sheetOpen) {
      setDraft(skuToDraft(editing));
    } else if (!sheetOpen) {
      setDraft(null);
    }
  }, [editing, sheetOpen]);

  const openEdit = (row: SKU) => {
    setEditing(row);
    setSheetOpen(true);
  };

  const handleSave = async() => {
    if (!editing || !draft) return;
    try {
      await patchSKU({ id: editing.id, data: draftToPayload(draft) }).unwrap();
      dispatch(showNotification({ summary: "Product updated", theme: "success" }));
      setSheetOpen(false);
      setEditing(null);
    } catch {
      // Errors are surfaced by the API layer.
    }
  };

  const handleSync = async() => {
    try {
      const message = await syncSKUs().unwrap();
      dispatch(showNotification({ summary: message, theme: "success" }));
    } catch {
      // Errors are surfaced by the API layer.
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            Products
          </h1>
          <p className="text-sm text-gray-600">
            Manage billing SKUs synced from your catalogue. Use sync to refresh from the integration.
          </p>
        </div>
        <Button
          type="button"
          variant="black"
          size="sm"
          disabled={isSyncing}
          onClick={() => { void handleSync(); }}
          className="shrink-0 gap-1.5 px-3 py-1"
        >
          <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
          Sync SKUs
        </Button>
      </div>

      {isLoading && (
        <p className="text-sm text-gray-600">
          Loading products…
        </p>
      )}
      {isError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load products.
          {" "}
          <button
            type="button"
            className="underline"
            onClick={() => { void refetch(); }}
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="min-h-0 flex-1 overflow-auto rounded-md border border-gray-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium text-gray-700">
                  Code
                </TableHead>
                <TableHead className="font-medium text-gray-700">
                  Name
                </TableHead>
                <TableHead className="font-medium text-gray-700">
                  External ID
                </TableHead>
                <TableHead className="font-medium text-gray-700">
                  Bill type
                </TableHead>
                <TableHead className="font-medium text-gray-700">
                  Qty is one
                </TableHead>
                <TableHead className="font-medium text-gray-700">
                  Status
                </TableHead>
                <TableHead className="font-medium text-gray-700">
                  Updated
                </TableHead>
                <TableHead className="w-[100px] font-medium text-gray-700 text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSkus.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-sm text-gray-500 py-8"
                  >
                    No SKUs returned.
                  </TableCell>
                </TableRow>
              ) : (
                sortedSkus.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs text-gray-900">
                      {row.sku ?? "—"}
                    </TableCell>
                    <TableCell className="text-gray-900">
                      {row.name ?? "—"}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {row.external_sku_id ?? "—"}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {row.bill_type ?? "—"}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {row.qty_is_one ? "Yes" : "No"}
                    </TableCell>
                    <TableCell className="text-gray-700">
                      {row.status ?? "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-gray-700 text-xs">
                      {(() => {
                        try {
                          return format(parseISO(row.updated_at), "PP p");
                        } catch {
                          return row.updated_at;
                        }
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="smallIcon"
                        className="text-gray-700"
                        aria-label="Edit product"
                        onClick={() => openEdit(row)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setEditing(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto bg-white"
        >
          <SheetHeader>
            <SheetTitle>
              Edit product
            </SheetTitle>
            <SheetDescription>
              Changes are saved to the billing SKU record. Read-only fields are shown for reference.
            </SheetDescription>
          </SheetHeader>

          {draft && editing && (
            <div className="mt-6 flex flex-col gap-4">
              <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                <div>
                  <span className="font-medium text-gray-700">
                    ID:
                  </span>
                  {" "}
                  {editing.id}
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Created:
                  </span>
                  {" "}
                  {editing.created_at}
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Updated:
                  </span>
                  {" "}
                  {editing.updated_at}
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    SKU code:
                  </span>
                  {" "}
                  {editing.sku ?? "—"}
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    Name:
                  </span>
                  {" "}
                  {editing.name ?? "—"}
                </div>
                <div>
                  <span className="font-medium text-gray-700">
                    External SKU ID:
                  </span>
                  {" "}
                  {editing.external_sku_id ?? "—"}
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-700">
                  Status
                </span>
                <BorderedSelect
                  options={statusOptions}
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                  placeholder="Select status"
                  fullWidth
                  containerMaxWidth="100%"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-gray-700">
                  Bill type
                </span>
                <BorderedSelect
                  options={billTypeOptions}
                  value={draft.bill_type}
                  onChange={(e) => setDraft({ ...draft, bill_type: e.target.value })}
                  placeholder="Select bill type"
                  fullWidth
                  containerMaxWidth="100%"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={draft.qty_is_one}
                  onChange={(e) => setDraft({ ...draft, qty_is_one: e.target.checked })}
                />

                <span>
                  Qty is one
                </span>
              </label>
              <BorderedTextarea
                label="Rules"
                className="rounded-md border border-gray-300 bg-white"
                value={draft.keywords}
                onChange={(e) => setDraft({ ...draft, keywords: e.target.value })}
                rows={3}
              />
            </div>
          )}

          <SheetFooter className="mt-8 gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="px-3 py-1 text-sm"
              onClick={() => setSheetOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="px-3 py-1 text-sm"
              onClick={() => { void handleSave(); }}
              disabled={isSaving || !draft}
            >
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ProductsTab;
